import { useCallback, useRef, useState } from 'react';
import { InteractionManager } from 'react-native';
import type { NavigationProp } from '@react-navigation/native';
import type { Product } from '../../../catalog/domain-api';
import { findProductByPlu, lookupProductByBarcode } from '../../../catalog/infra-api'
import { normalizeProductCode } from '../../../catalog/infra-api'
import { productRequiresWeightInput } from '../../../catalog/domain-api'
import type { RemoteBarcodeHit } from '../../../catalog/domain-api';
import { sourceLabels } from '../../../inventory/application/inventoryUtils';
import type { MasterFilterOption } from '../../../shared/filters/MasterFilterSheetModal';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { hapticAddToCart } from '../../../shared/ui/posHaptics';
import { posBarcodeScanFilterResetAfterHit } from '../../application/posBarcodeScanFilters';
import { buildPosVariantLineProduct } from '../../application/posVariantLineProduct';
import type { AddProductToCartOptions } from '../../store/usePosStore';
import { parseScaleBarcodeProfile, parseWeightBarcode, weightBarcodeToCartQuantity } from '@shared/barcode';
import { normalizeUnitOfMeasure } from '@shared/quantity';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';

type ScanMissSheetState = {
  title: string;
  options: MasterFilterOption[];
};

type Params = {
  navigation: NavigationProp<Record<string, object | undefined>>;
  currentBranchId: string | null | undefined;
  setScannerVisible: (v: boolean) => void;
  setSearchQuery: (q: string) => void;
  setCategory: (c: Product['categoryId'] | 'all') => void;
  setLookupFeedback: (s: string | null) => void;
  setVariosVisible: (v: boolean) => void;
  handleProductPress: (product: Product) => void;
  addProductToCart: (product: Product, quantityOrOpts?: number | AddProductToCartOptions) => void;
  canImportFromCatalog: boolean;
  onRemoteHit: (hit: RemoteBarcodeHit) => void;
};

export function usePosBarcodeScanHandler({
  navigation,
  currentBranchId,
  setScannerVisible,
  setSearchQuery,
  setCategory,
  setLookupFeedback,
  setVariosVisible,
  handleProductPress,
  addProductToCart,
  canImportFromCatalog,
  onRemoteHit,
}: Params) {
  const [scanMissSheet, setScanMissSheet] = useState<ScanMissSheetState | null>(null);
  const scanMissActionRef = useRef<((id: string) => void) | null>(null);
  const isProcessingRef = useRef(false);
  const scaleBarcodeProfile = useSessionStore((s) => {
    const branch = s.branches.find((b) => b.id === (currentBranchId ?? s.currentBranchId));
    return parseScaleBarcodeProfile(branch?.scaleBarcodeProfile);
  });

  const closeScanMissSheet = useCallback(() => {
    setScanMissSheet(null);
    scanMissActionRef.current = null;
  }, []);

  const openScanMissSheet = useCallback(
    (title: string, options: MasterFilterOption[], onPick: (id: string) => void) => {
      scanMissActionRef.current = onPick;
      setScanMissSheet({ title, options });
    },
    []
  );

  const handleScanMissSelect = useCallback(
    (id: string | null) => {
      const action = scanMissActionRef.current;
      closeScanMissSheet();
      if (id && action) action(id);
    },
    [closeScanMissSheet]
  );

  const handleScannerResult = useCallback(
    async (rawCode: string) => {
      const trimmed = rawCode.trim();
      if (!trimmed) return;
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      try {
      const displayCode = normalizeProductCode(trimmed);
      setScannerVisible(false);
      setSearchQuery(displayCode);

        const weightParsed = parseWeightBarcode(trimmed, scaleBarcodeProfile);
        if (weightParsed) {
          const weightProduct = await findProductByPlu({
            plu: weightParsed.plu,
            branchId: currentBranchId ?? null,
          });
          if (weightProduct && productRequiresWeightInput({ unitOfMeasure: weightProduct.unitOfMeasure ?? 'unit' })) {
            const uom = normalizeUnitOfMeasure(weightProduct.unitOfMeasure);
            if (uom === 'kg' || uom === 'g') {
              const quantity = weightBarcodeToCartQuantity(
                weightParsed,
                weightProduct.price,
                uom,
              );
              if (quantity != null && quantity > 0) {
                const reset = posBarcodeScanFilterResetAfterHit();
                setSearchQuery(reset.searchQuery);
                setCategory(reset.category);
                hapticAddToCart();
                addProductToCart(weightProduct, { quantity });
                setLookupFeedback(`Último escaneo: ${weightProduct.name}`);
                return;
              }
            }
          }
        }

        const result = await lookupProductByBarcode({ code: trimmed, branchId: currentBranchId ?? null });

        if (result.kind === 'local_hit') {
          const reset = posBarcodeScanFilterResetAfterHit();
          setSearchQuery(reset.searchQuery);
          setCategory(reset.category);
          InteractionManager.runAfterInteractions(() => {
            handleProductPress(result.product);
          });
          setLookupFeedback(`Último escaneo: ${result.product.name}`);
          return;
        }

        if (result.kind === 'local_variant_hit') {
          const reset = posBarcodeScanFilterResetAfterHit();
          setSearchQuery(reset.searchQuery);
          setCategory(reset.category);
          const variantProduct = buildPosVariantLineProduct(result.product, result.variant);
          hapticAddToCart();
          addProductToCart(variantProduct, { quantity: 1, variantId: result.variant.id });
          setLookupFeedback(`Último escaneo: ${variantProduct.name}`);
          return;
        }

        // Miss / remoto: dejar el código visible para que se vea qué falló.
        setSearchQuery(result.barcode);

        if (result.kind === 'remote_hit') {
          setLookupFeedback(null);
          const sourceLabel = sourceLabels[result.source] ?? result.source;

          if (canImportFromCatalog) {
            setLookupFeedback(`Encontramos online: ${result.suggestion.name} (${sourceLabel})`);
            onRemoteHit(result);
            return;
          }

          const message = `Encontramos "${result.suggestion.name}" en ${sourceLabel}. Para venderlo, confirmalo en Catálogo (hace falta permiso de edición).`;
          openScanMissSheet(
            'Ítem encontrado online',
            [
              { id: 'varios', name: 'Venta Varios', subtitle: message },
              { id: 'catalog', name: 'Abrir Catálogo' },
            ],
            (id) => {
              if (id === 'varios') setVariosVisible(true);
              if (id === 'catalog') navigation.navigate('Catalog');
            }
          );
          return;
        }

        if (result.kind === 'offline_miss') {
          setLookupFeedback(null);
          openScanMissSheet(
            'Sin conexión',
            [
              {
                id: 'varios',
                name: 'Venta Varios',
                subtitle:
                  'No encontramos ese ítem acá. Buscalo en la lista, vendé como Varios o sincronizá cuando haya señal.',
              },
            ],
            (id) => {
              if (id === 'varios') setVariosVisible(true);
            }
          );
          return;
        }

        setLookupFeedback(null);
        openScanMissSheet(
          'Ítem no encontrado',
          [
            {
              id: 'varios',
              name: 'Venta Varios',
              subtitle:
                'No encontramos ese código. Cargalo en Catálogo o vendelo como Varios.',
            },
            { id: 'catalog', name: 'Abrir Catálogo' },
          ],
          (id) => {
            if (id === 'varios') setVariosVisible(true);
            if (id === 'catalog') navigation.navigate('Catalog');
          }
        );
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'pos', op: 'barcode_scan', error, userMessage: 'No pudimos buscar el código.' });
      } finally {
        isProcessingRef.current = false;
      }
    },
    [
      canImportFromCatalog,
      currentBranchId,
      scaleBarcodeProfile,
      handleProductPress,
      addProductToCart,
      navigation,
      onRemoteHit,
      openScanMissSheet,
      setCategory,
      setLookupFeedback,
      setScannerVisible,
      setSearchQuery,
      setVariosVisible,
    ]
  );

  const scanMissSheetProps = scanMissSheet
    ? {
        visible: true as const,
        title: scanMissSheet.title,
        options: scanMissSheet.options,
        selectedId: null as string | null,
        onSelect: handleScanMissSelect,
        onClose: closeScanMissSheet,
        showAllOption: false as const,
      }
    : null;

  return { handleScannerResult, scanMissSheetProps };
}
